import { createContext, useContext, useReducer, useRef, useCallback, useEffect, type ReactNode, type Dispatch } from "react";
import type { FaustCompiler, FaustSvgDiagrams, FaustAudioWorkletNode, FaustScriptProcessorNode, LibFaust, AudioData } from "@grame/faustwasm";
import { toast } from "sonner";
import { Analyser } from "../Analyser";
import { Recorder } from "../Recorder";
import { safeStorage } from "../lib/utils";
import VERSION from "../version";

// ── Types ──────────────────────────────────────────────────────────────────

export type CompileOptions = {
    useWorklet: boolean;
    useDouble: boolean;
    bufferSize: 128 | 256 | 512 | 1024 | 2048 | 4096;
    saveCode: boolean;
    saveParams: boolean;
    saveDsp: boolean;
    popup: boolean;
    voices: number;
    realtimeCompile: boolean;
    plotMode: "offline" | "continuous" | "onevent" | "manual";
    plot: number;
    plotSR: number;
    plotFFT: number;
    plotFFTOverlap: number;
    drawSpectrogram: boolean;
    enableGuiBuilder: boolean;
    guiBuilderUrl: string;
    exportPlatform: string;
    exportArch: string;
    mainFile?: string;
    args: string[];
};

export type AudioEnv = {
    audioCtx?: AudioContext;
    gainInput?: GainNode;
    gainUIInput?: any;
    meterInput?: any;
    splitterInput?: ChannelSplitterNode;
    analyserInput?: AnalyserNode;
    splitterOutput?: ChannelSplitterNode;
    analyserOutput?: AnalyserNode;
    inputs?: Record<string, MediaStreamAudioSourceNode | MediaElementAudioSourceNode>;
    currentInput?: string;
    destination?: MediaStreamAudioDestinationNode | AudioDestinationNode;
    dsp?: FaustScriptProcessorNode | FaustAudioWorkletNode;
    dspConnectedToOutput: boolean;
    dspConnectedToInput: boolean;
    inputEnabled: boolean;
    outputEnabled: boolean;
};

export type FaustState = {
    isReady: boolean;
    isCompiling: boolean;
    hasDsp: boolean;
    outputEnabled: boolean;
    dspInfo: { inputs: number; outputs: number; params: number; isWorklet: boolean } | null;
    currentFile: string;
    fileList: string[];
    diagramSvg: string | null;
    showLeftPanel: boolean;
    showRightPanel: boolean;
    activeBottomTab: string;
    recorderEnabled: boolean;
    recorderTime: string;
};

type FaustAction =
    | { type: "SET_READY" }
    | { type: "SET_COMPILING"; value: boolean }
    | { type: "SET_DSP_INFO"; info: FaustState["dspInfo"] }
    | { type: "SET_OUTPUT_ENABLED"; value: boolean }
    | { type: "SET_CURRENT_FILE"; name: string }
    | { type: "SET_FILE_LIST"; files: string[] }
    | { type: "SET_DIAGRAM_SVG"; svg: string | null }
    | { type: "TOGGLE_LEFT_PANEL" }
    | { type: "TOGGLE_RIGHT_PANEL" }
    | { type: "SET_ACTIVE_BOTTOM_TAB"; tab: string }
    | { type: "SET_RECORDER_ENABLED"; value: boolean }
    | { type: "SET_RECORDER_TIME"; time: string }
    | { type: "SET_HAS_DSP"; value: boolean };

// ── Reducer ────────────────────────────────────────────────────────────────

const initialState: FaustState = {
    isReady: false,
    isCompiling: false,
    hasDsp: false,
    outputEnabled: false,
    dspInfo: null,
    currentFile: "",
    fileList: [],
    diagramSvg: null,
    showLeftPanel: true,
    showRightPanel: true,
    activeBottomTab: "diagram",
    recorderEnabled: false,
    recorderTime: "0:00.000",
};

function faustReducer(state: FaustState, action: FaustAction): FaustState {
    switch (action.type) {
        case "SET_READY":
            return { ...state, isReady: true };
        case "SET_COMPILING":
            return { ...state, isCompiling: action.value };
        case "SET_DSP_INFO":
            return { ...state, dspInfo: action.info, hasDsp: action.info !== null };
        case "SET_OUTPUT_ENABLED":
            return { ...state, outputEnabled: action.value };
        case "SET_CURRENT_FILE":
            return { ...state, currentFile: action.name };
        case "SET_FILE_LIST":
            return { ...state, fileList: action.files };
        case "SET_DIAGRAM_SVG":
            return { ...state, diagramSvg: action.svg };
        case "TOGGLE_LEFT_PANEL":
            return { ...state, showLeftPanel: !state.showLeftPanel };
        case "TOGGLE_RIGHT_PANEL":
            return { ...state, showRightPanel: !state.showRightPanel };
        case "SET_ACTIVE_BOTTOM_TAB":
            return { ...state, activeBottomTab: action.tab };
        case "SET_RECORDER_ENABLED":
            return { ...state, recorderEnabled: action.value };
        case "SET_RECORDER_TIME":
            return { ...state, recorderTime: action.time };
        case "SET_HAS_DSP":
            return { ...state, hasDsp: action.value };
        default:
            return state;
    }
}

// ── Context ────────────────────────────────────────────────────────────────

type FaustContextValue = {
    state: FaustState;
    dispatch: Dispatch<FaustAction>;
    // Mutable refs for audio/compiler objects (not suitable for React state)
    audioEnvRef: React.MutableRefObject<AudioEnv>;
    compileOptionsRef: React.MutableRefObject<CompileOptions>;
    compilerRef: React.MutableRefObject<FaustCompiler | null>;
    libFaustRef: React.MutableRefObject<LibFaust | null>;
    svgDiagramsRef: React.MutableRefObject<FaustSvgDiagrams | null>;
    editorRef: React.MutableRefObject<any>;
    analyserRef: React.MutableRefObject<Analyser>;
    recorderRef: React.MutableRefObject<Recorder>;
    bfsRef: React.MutableRefObject<any>;
    dspParamsRef: React.MutableRefObject<Record<string, number>>;
    serverRef: React.MutableRefObject<string>;
    // Actions
    runDsp: (code: string) => Promise<{ success: boolean; error?: Error }>;
    stopDsp: () => void;
    updateDiagram: (code: string) => { success: boolean; error?: Error };
    toggleOutput: () => Promise<void>;
    saveCompileOptions: () => void;
};

const FaustContext = createContext<FaustContextValue | null>(null);

export function useFaust() {
    const ctx = useContext(FaustContext);
    if (!ctx) throw new Error("useFaust must be used within FaustProvider");
    return ctx;
}

// ── Default compile options ────────────────────────────────────────────────

const PROJECT_DIR = "/usr/share/project/";

function loadEditorParams(): Partial<CompileOptions> {
    const clientVersion = safeStorage.getItem("faust_editor_version");
    if (clientVersion !== VERSION) return {};
    const str = safeStorage.getItem("faust_editor_params");
    if (!str) return {};
    try {
        return JSON.parse(str) as Partial<CompileOptions>;
    } catch {
        return {};
    }
}

function loadDspParams(): Record<string, number> {
    const str = safeStorage.getItem("faust_editor_dsp_params");
    if (!str) return {};
    try {
        return JSON.parse(str);
    } catch {
        return {};
    }
}

const defaultCompileOptions: CompileOptions = {
    useWorklet: !!window.AudioWorklet,
    useDouble: false,
    bufferSize: 1024,
    saveCode: true,
    saveParams: false,
    saveDsp: false,
    popup: false,
    voices: 0,
    realtimeCompile: false,
    plotMode: "offline",
    plot: 256,
    plotSR: 48000,
    plotFFT: 256,
    plotFFTOverlap: 2,
    drawSpectrogram: false,
    enableGuiBuilder: false,
    guiBuilderUrl: "https://mainline.i3s.unice.fr/fausteditorweb/dist/PedalEditor/Front-End/",
    exportPlatform: "source",
    exportArch: "cplusplus",
    ...loadEditorParams(),
    args: ["-f", "10", "-I", PROJECT_DIR],
};

// ── Provider ───────────────────────────────────────────────────────────────

export function FaustProvider({ children }: { children: ReactNode }) {
    const [state, dispatch] = useReducer(faustReducer, initialState);

    const audioEnvRef = useRef<AudioEnv>({
        dspConnectedToInput: false,
        dspConnectedToOutput: false,
        inputEnabled: false,
        outputEnabled: false,
    });
    const isCompilingRef = useRef(false);
    const compileOptionsRef = useRef<CompileOptions>({ ...defaultCompileOptions });
    const compilerRef = useRef<FaustCompiler | null>(null);
    const libFaustRef = useRef<LibFaust | null>(null);
    const svgDiagramsRef = useRef<FaustSvgDiagrams | null>(null);
    const editorRef = useRef<any>(null);
    const analyserRef = useRef<Analyser>(new Analyser(16, "continuous"));
    const recorderRef = useRef<Recorder>(new Recorder());
    const bfsRef = useRef<any>(null);
    const dspParamsRef = useRef<Record<string, number>>(loadDspParams());
    const serverRef = useRef<string>("https://faustservice.inria.fr");

    const saveCompileOptions = useCallback(() => {
        const str = JSON.stringify(compileOptionsRef.current);
        safeStorage.setItem("faust_editor_params", str);
    }, []);

    const showError = useCallback((e: Error | string) => {
        console.error(e);
        toast.error("Faust Error", {
            description: e instanceof Error ? e.message : e,
        });
    }, []);

    const updateDiagram = useCallback((code: string): { success: boolean; error?: Error } => {
        const compiler = compilerRef.current;
        const diagrams = svgDiagramsRef.current;
        if (!compiler || !diagrams) return { success: false, error: new Error("Compiler not ready") };

        const args = compileOptionsRef.current.args.slice();
        if (compileOptionsRef.current.useDouble) args.push("-double");
        try {
            const strSvg = diagrams.from("main", code, args.join(" "))["process.svg"];
            dispatch({ type: "SET_DIAGRAM_SVG", svg: strSvg });
            return { success: true };
        } catch (e: any) {
            showError(e);
            return { error: e, success: false };
        }
    }, [showError]);

    const loadSoundfiles = useCallback(async (audioCtx: BaseAudioContext, soundfileList: string[]): Promise<Record<string, AudioData>> => {
        const map = {} as Record<string, AudioData>;
        const libFaust = libFaustRef.current;
        if (!libFaust) return map;
        const files = libFaust.fs().readdir(PROJECT_DIR) as string[];
        await Promise.all(
            files
                .filter((n) => soundfileList.indexOf(n) !== -1)
                .map(async (filename) => {
                    const ui8Array = libFaust.fs().readFile(PROJECT_DIR + filename);
                    try {
                        const audioBuffer = await audioCtx.decodeAudioData(ui8Array.buffer);
                        map[filename] = {
                            audioBuffer: new Array(audioBuffer.numberOfChannels).fill(0).map((_, i) => audioBuffer.getChannelData(i)),
                            sampleRate: audioBuffer.sampleRate,
                        } as AudioData;
                    } catch {
                        // skip undecodable files
                    }
                })
        );
        return map;
    }, []);

    const runDsp = useCallback(async (codeIn: string): Promise<{ success: boolean; error?: Error }> => {
        if (isCompilingRef.current) return { success: false, error: new Error("Another DSP is compiling") };
        isCompilingRef.current = true;
        dispatch({ type: "SET_COMPILING", value: true });

        const done = (result: { success: boolean; error?: Error }) => {
            isCompilingRef.current = false;
            dispatch({ type: "SET_COMPILING", value: false });
            return result;
        };

        const compileToastId = toast.loading("Compiling DSP...");

        try {
            const compiler = compilerRef.current;
            if (!compiler) {
                toast.dismiss(compileToastId);
                return done({ success: false, error: new Error("Compiler not ready") });
            }

            const audioEnv = audioEnvRef.current;
            const opts = compileOptionsRef.current;

            // Prepend filename/name declarations like the original Faust IDE does
            const code = `declare filename "main.dsp"; declare name "main"; ${codeIn}`;

            const args = opts.args.slice();
            if (opts.useDouble) args.push("-double");

            const {
                FaustMonoDspGenerator,
                FaustPolyDspGenerator,
                FaustCompiler: FC,
            } = await import("@grame/faustwasm");

            // Ensure audio context exists
            if (!audioEnv.audioCtx) {
                const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ latencyHint: 0.00001 });
                audioEnv.audioCtx = audioCtx;
                audioEnv.outputEnabled = true;
                if (!audioEnv.destination) {
                    audioEnv.destination = audioCtx.destination;
                    audioEnv.destination.channelCount = audioEnv.destination.maxChannelCount;
                    audioEnv.destination.channelInterpretation = "discrete";
                }
                dispatch({ type: "SET_OUTPUT_ENABLED", value: true });
            }
            if (audioEnv.audioCtx.state !== "running") await audioEnv.audioCtx.resume();

            // Plot handler for recorder + analyser
            const plotHandler = (plotted: Float32Array[], index: number, events?: { type: string; data: any }[]) => {
                analyserRef.current.plotHandler(plotted, index, events);
                const recorder = recorderRef.current;
                if (!recorder.enabled) return;
                const t = recorder.append(plotted, index);
                const d = new Date(t * 1000);
                const min = d.getMinutes();
                const sec = `0${d.getSeconds()}`.slice(-2);
                const ms = `00${d.getMilliseconds()}`.slice(-3);
                dispatch({ type: "SET_RECORDER_TIME", time: `${min}:${sec}.${ms}` });
            };

            // Compile the DSP
            let node: FaustScriptProcessorNode | FaustAudioWorkletNode;
            if (opts.voices) {
                const factory = await new FaustPolyDspGenerator().compile(compiler, "main", code, args.join(" "));
                const soundfileList = factory.getSoundfileList();
                const soundfiles = await loadSoundfiles(audioEnv.audioCtx, soundfileList);
                factory.addSoundfiles(soundfiles);
                node = await factory.createNode(audioEnv.audioCtx, opts.voices, "main", undefined, undefined, undefined, !opts.useWorklet, opts.bufferSize);
            } else {
                const factory = await new FaustMonoDspGenerator().compile(compiler, "main", code, args.join(" "));
                const soundfileList = factory.getSoundfileList();
                const soundfiles = await loadSoundfiles(audioEnv.audioCtx, soundfileList);
                factory.addSoundfiles(soundfiles);
                node = await factory.createNode(audioEnv.audioCtx, "main", undefined, !opts.useWorklet, opts.bufferSize);
            }
            if (!node) throw new Error("Unknown Error in WebAudio Node.");
            node.setPlotHandler(plotHandler);
            node.startSensors();

            // Disconnect previous DSP
            if (audioEnv.dsp) {
                if (audioEnv.dspConnectedToInput && audioEnv.gainInput) {
                    audioEnv.gainInput.disconnect(audioEnv.dsp);
                    audioEnv.dspConnectedToInput = false;
                }
                audioEnv.dsp.disconnect();
                audioEnv.dspConnectedToOutput = false;
                audioEnv.dsp.destroy();
                delete audioEnv.dsp;
            }

            // Restore saved params
            if (opts.saveParams) {
                const dspParams = dspParamsRef.current;
                for (const path in dspParams) {
                    if (node.getParams().indexOf(path) !== -1) {
                        node.setParamValue(path, dspParams[path]);
                    }
                }
            }

            await audioEnv.audioCtx.resume();

            // Connect DSP to audio graph
            audioEnv.dsp = node;
            const channelsCount = node.getNumOutputs();

            if (!audioEnv.analyserOutput) {
                audioEnv.analyserOutput = audioEnv.audioCtx.createAnalyser();
            }
            if (!audioEnv.splitterOutput || audioEnv.splitterOutput.numberOfOutputs !== channelsCount) {
                if (audioEnv.splitterOutput) audioEnv.splitterOutput.disconnect();
                audioEnv.splitterOutput = audioEnv.audioCtx.createChannelSplitter(channelsCount);
                audioEnv.splitterOutput.connect(audioEnv.analyserOutput, 0);
            }

            if (audioEnv.gainInput && node.getNumInputs()) {
                audioEnv.gainInput.connect(node);
                audioEnv.dspConnectedToInput = true;
            }
            node.connect(audioEnv.splitterOutput);

            // Connect to output destination
            if (!audioEnv.destination) {
                audioEnv.destination = audioEnv.audioCtx.destination;
                audioEnv.destination.channelCount = audioEnv.destination.maxChannelCount;
                audioEnv.destination.channelInterpretation = "discrete";
            }
            if (audioEnv.outputEnabled) {
                node.connect(audioEnv.destination);
                audioEnv.dspConnectedToOutput = true;
            }

            // Param handler: forward DSP output params to iframe
            node.setOutputParamHandler((path: string, value: number) => {
                const msg = { path, value, type: "param" };
                const iframe = document.getElementById("iframe-faust-ui") as HTMLIFrameElement | null;
                if (iframe?.contentWindow) iframe.contentWindow.postMessage(msg, "*");
            });

            // Send UI definition to the DSP UI iframe so it renders controls
            const bindUI = () => {
                const iframe = document.getElementById("iframe-faust-ui") as HTMLIFrameElement | null;
                if (!iframe?.contentWindow) return;
                iframe.contentWindow.postMessage({ type: "ui", ui: node.getUI() }, "*");
                if (opts.saveParams) {
                    const params = node.getParams();
                    const dspParams = dspParamsRef.current;
                    for (const path in dspParams) {
                        if (params.indexOf(path) !== -1) {
                            iframe.contentWindow.postMessage({ path, value: dspParams[path], type: "param" }, "*");
                        }
                    }
                }
            };
            // Retry bindUI a few times -- iframe may need a moment after tab switch
            setTimeout(bindUI, 150);
            setTimeout(bindUI, 500);

            toast.success("Compilation complete", {
                id: compileToastId,
                description: `${node.getNumInputs()} in / ${node.getNumOutputs()} out / ${node.getParams().length} params`,
            });

            dispatch({
                type: "SET_DSP_INFO",
                info: {
                    inputs: node.getNumInputs(),
                    outputs: node.getNumOutputs(),
                    params: node.getParams().length,
                    isWorklet: !(node instanceof ScriptProcessorNode),
                },
            });

            // Save DSP factory cache (can throw stack overflow on large factories)
            try {
                safeStorage.setItem("faust_editor_dsp_table", FC.stringifyDSPFactories());
            } catch (cacheErr) {
                console.warn("Could not cache DSP factory:", cacheErr);
            }

            return done({ success: true });
        } catch (e: any) {
            toast.error("Compilation failed", {
                id: compileToastId,
                description: e instanceof Error ? e.message : String(e),
            });
            console.error(e);
            return done({ success: false, error: e });
        }
    }, [loadSoundfiles]);

    const stopDsp = useCallback(() => {
        const audioEnv = audioEnvRef.current;
        if (audioEnv.dsp) {
            if (audioEnv.dspConnectedToInput && audioEnv.gainInput) {
                audioEnv.gainInput.disconnect(audioEnv.dsp);
                audioEnv.dspConnectedToInput = false;
            }
            audioEnv.dsp.disconnect();
            audioEnv.dspConnectedToOutput = false;
            audioEnv.dsp.destroy();
            delete audioEnv.dsp;
        }
        dispatch({ type: "SET_DSP_INFO", info: null });
    }, []);

    const toggleOutput = useCallback(async () => {
        const audioEnv = audioEnvRef.current;
        if (audioEnv.outputEnabled) {
            audioEnv.outputEnabled = false;
            if (audioEnv.dspConnectedToOutput && audioEnv.dsp && audioEnv.destination) {
                audioEnv.dsp.disconnect(audioEnv.destination);
                audioEnv.dspConnectedToOutput = false;
            }
            dispatch({ type: "SET_OUTPUT_ENABLED", value: false });
        } else {
            audioEnv.outputEnabled = true;
            if (!audioEnv.audioCtx) {
                const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ latencyHint: 0.00001 });
                audioEnv.audioCtx = audioCtx;
            }
            if (!audioEnv.destination) {
                audioEnv.destination = audioEnv.audioCtx.destination;
                audioEnv.destination.channelCount = audioEnv.destination.maxChannelCount;
                audioEnv.destination.channelInterpretation = "discrete";
            }
            if (audioEnv.dsp) {
                audioEnv.dsp.connect(audioEnv.destination);
                audioEnv.dspConnectedToOutput = true;
            }
            dispatch({ type: "SET_OUTPUT_ENABLED", value: true });
        }
    }, []);

    // Global message listener for DSP UI iframe param changes
    useEffect(() => {
        const handleMessage = (e: MessageEvent) => {
            if (!e.data?.type) return;
            if (e.data.type === "param") {
                const audioEnv = audioEnvRef.current;
                if (audioEnv.dsp) audioEnv.dsp.setParamValue(e.data.path, +e.data.value);
                dspParamsRef.current[e.data.path] = +e.data.value;
                if (compileOptionsRef.current.saveParams) {
                    safeStorage.setItem("faust_editor_dsp_params", JSON.stringify(dspParamsRef.current));
                }
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    const value: FaustContextValue = {
        state,
        dispatch,
        audioEnvRef,
        compileOptionsRef,
        compilerRef,
        libFaustRef,
        svgDiagramsRef,
        editorRef,
        analyserRef,
        recorderRef,
        bfsRef,
        dspParamsRef,
        serverRef,
        runDsp,
        stopDsp,
        updateDiagram,
        toggleOutput,
        saveCompileOptions,
    };

    return <FaustContext.Provider value={value}>{children}</FaustContext.Provider>;
}

export { PROJECT_DIR };
